import {z} from 'zod'

import {CreateOrUpdateBlockKeyFactDocument} from '@sdox/api'

import {EditorFeature} from '../../../../inputs/TipTap/types'
import {updateMetaSchema} from '../../utils/getUpdateMetaFromFieldValues'
import {createFormConfig} from '../../utils/createFormConfig'
import {optionalButtonSchema} from '../../utils/schema'
import {buttonFormToInput, buttonFragmentToForm} from '../../utils/buttonMappers'

import type {ButtonFormSchema} from '../../utils/schema'
import type {BlockKeyFactInput, CreateOrUpdateBlockKeyFactMutation, XcNwBlock_KeyFactTypeBlockRowType_Fragment} from '@sdox/api'

type UpdateKeyFactsValidatorSchema = z.infer<typeof updateKeyFactsValidatorSchema>

const updateKeyFactsValidatorSchema = z.object({
  data: z.array(
    z.object({
      // image is selected as XcMediaRef from MediaLibrary
      image: z.object({
        // md5 hashes are actually 16 bytes long, but if the hash algorithm changes
        //  we would have a very hard to find bug if we expect a hard 16 chars.
        fileHash: z.string().min(1),
      }),
      text: z.string().min(1),
      title: z.string().min(1),
      cta: optionalButtonSchema,
    }).required(),
  ),
  meta: updateMetaSchema,
})

export const keyFactsConfig = createFormConfig<
  UpdateKeyFactsValidatorSchema,
  CreateOrUpdateBlockKeyFactMutation,
  BlockKeyFactInput[],
  XcNwBlock_KeyFactTypeBlockRowType_Fragment
>({
  mutationOptions: {
    mutation: CreateOrUpdateBlockKeyFactDocument,
  },
  fieldMapper: (fieldValues) => {
    if (!Array.isArray(fieldValues.data)) {
      throw new Error('field values must have a data array')
    }

    return fieldValues.data.map<BlockKeyFactInput>((item) => ({
      title: item.title,
      text: item.text,
      fileHash: item.image.fileHash,
      // TODO: needs to be implemented in the form in a future iteration
      mediaDivider: false,
      cta: buttonFormToInput(item.cta as ButtonFormSchema),
    }))
  },
  validatorSchema: updateKeyFactsValidatorSchema,
  inputFields: [
    {
      label: 'Key Fact',
      inputType: 'array',
      required: true,
      description: '',
      fieldName: 'data',
      defaultValue: undefined,
      fields: [
        {
          label: 'Image',
          fieldName: 'image',
          inputType: 'image',
          defaultValue: '',
          required: true,
        },
        {
          label: 'Title',
          fieldName: 'title',
          inputType: 'richtext',
          editorConfig: {
            spanAsParagraph: true,
            features: [
              EditorFeature.nobreak,
              EditorFeature.footnote,
            ],
          },
          defaultValue: '',
          required: true,
        },
        {
          label: 'Text',
          fieldName: 'text',
          inputType: 'richtext',
          editorConfig: {
            features: [
              EditorFeature.footnote,
              EditorFeature.linebreak,
              EditorFeature.nobreak,
              EditorFeature.bold,
              EditorFeature.italic,
            ],
          },
          defaultValue: '',
          required: true,
        },
        {
          label: 'Button',
          fieldName: 'cta',
          inputType: 'button',
          defaultValue: 'none',
          required: false,
        },
      ],
    },
  ],
  getDefaultValues: (block) => {
    const data = block.items.map(({title, description, cta, image}) => ({
      title,
      text: description ?? '',
      image: {
        fileHash: image?.fileHash ?? '',
      },
      cta: buttonFragmentToForm(cta),
    }))

    return {
      data,
      meta: {
        pullout: block.pullout,
        countryCodes: block.countryCodes,
      },
    }
  },
})
